/***
*
*   WORKSPACES LIST
*   View and manage all organizations/workspaces
*
**********/

import { useState, useContext, useEffect } from 'react';
import {
  Animate,
  Card,
  Table,
  Button,
  Loader,
  ViewContext,
  useNavigate,
  Paginate,
  usePlans
} from 'components/lib';
import {
  useWorkspaces,
  useWorkspaceMutations
} from 'components/hooks/useWorkspaceManagement';

const Style = {
  pageHeader: 'flex justify-between items-start mb-6',
  pageTitle: 'text-2xl font-bold text-slate-800',
  pageSubtitle: 'text-sm text-slate-500 mt-1',
  filtersContainer: 'flex flex-wrap gap-4 p-4',
  filterGroup: 'flex-1 min-w-[200px]',
  searchInput: 'w-full p-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500',
  filterSelect: 'w-full p-3 border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-500',
  statsRow: 'flex gap-4 mb-6',
  statItem: 'bg-white rounded-lg border border-slate-200 p-4 flex flex-col',
  statValue: 'text-2xl font-bold text-slate-800',
  statLabel: 'text-sm text-slate-500',
};

export function WorkspacesList(props) {
  const context = useContext(ViewContext);
  const navigate = useNavigate();
  const plans = usePlans();
  const mutations = useWorkspaceMutations();

  // Filters state
  const [filters, setFilters] = useState({
    page: 1,
    limit: 25,
    search: '',
    plan: '',
    status: '',
    sort: 'createdAt',
    order: 'desc'
  });

  const [searchInput, setSearchInput] = useState('');

  const { data, loading, error, refetch } = useWorkspaces(filters);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchInput, page: 1 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (newOffset) => {
    const newPage = Math.floor(newOffset / filters.limit) + 1;
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleViewWorkspace = (workspace) => {
    navigate(`/workspaces/${workspace.id || workspace._id}`);
  };

  const handleToggleStatus = async (workspace) => {
    const action = workspace.active ? 'deactivate' : 'activate';
    const confirmText = workspace.active
      ? `Are you sure you want to deactivate "${workspace.name}"? Users will not be able to log in.`
      : `Are you sure you want to activate "${workspace.name}"?`;

    context.modal.show({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Workspace`,
      text: confirmText,
      buttonText: action.charAt(0).toUpperCase() + action.slice(1),
      destructive: workspace.active,
    }, async () => {
      try {
        if (workspace.active) {
          await mutations.deactivate(workspace.id || workspace._id);
        } else {
          await mutations.activate(workspace.id || workspace._id);
        }
        context.notification.show(`Workspace ${action}d successfully`, 'success', true);
        refetch();
      } catch (err) {
        context.notification.show(`Failed to ${action} workspace`, 'error', true);
      }
    });
  };

  const handleExportAll = async () => {
    try {
      await mutations.exportAll({ plan: filters.plan, status: filters.status });
      context.notification.show('Export started', 'success', true);
    } catch (err) {
      context.notification.show('Failed to export data', 'error', true);
    }
  };

  // Format data for table
  const tableData = data?.results?.map(ws => ({
    id: ws.id || ws._id,
    name: ws.name,
    slug: ws.slug,
    plan: ws.plan_name || ws.plan,
    status: ws.active ? 'Active' : 'Inactive',
    owner_email: ws.owner_email || 'N/A',
    user_count: ws.user_count || 0,
    employee_count: ws.employee_count || 0,
    created: new Date(ws.date_created || ws.createdAt).toLocaleDateString(),
    active: ws.active
  })) || [];

  if (error) {
    return (
      <Card>
        <div className="text-center py-8">
          <p className="text-red-500">Error loading workspaces</p>
          <p className="text-sm text-slate-400 mt-2">{error}</p>
        </div>
      </Card>
    );
  }

  return (
    <Animate type="pop">
      {/* Header */}
      <div className={Style.pageHeader}>
        <div>
          <h1 className={Style.pageTitle}>Organizations</h1>
          <p className={Style.pageSubtitle}>
            Manage all workspaces and organizations
          </p>
        </div>
        <Button
          text="Export All"
          icon="download"
          color="blue"
          action={handleExportAll}
        />
      </div>

      {/* Filters */}
      <Card>
        <div className={Style.filtersContainer}>
          <div className={Style.filterGroup}>
            <input
              type="text"
              placeholder="Search by name or slug..."
              className={Style.searchInput}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          <div className={Style.filterGroup}>
            <select
              className={Style.filterSelect}
              value={filters.plan}
              onChange={(e) => handleFilterChange('plan', e.target.value)}
            >
              <option value="">All Plans</option>
              {plans?.data?.list?.map(plan => (
                <option key={plan.value} value={plan.value}>{plan.label}</option>
              ))}
            </select>
          </div>

          <div className={Style.filterGroup}>
            <select
              className={Style.filterSelect}
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="trial">On Trial</option>
            </select>
          </div>

          <div className={Style.filterGroup}>
            <select
              className={Style.filterSelect}
              value={`${filters.sort}-${filters.order}`}
              onChange={(e) => {
                const [sort, order] = e.target.value.split('-');
                setFilters(prev => ({ ...prev, sort, order }));
              }}
            >
              <option value="createdAt-desc">Newest First</option>
              <option value="createdAt-asc">Oldest First</option>
              <option value="name-asc">Name A-Z</option>
              <option value="name-desc">Name Z-A</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Stats Summary */}
      {data && (
        <div className={Style.statsRow}>
          <div className={Style.statItem}>
            <span className={Style.statValue}>{data.total || 0}</span>
            <span className={Style.statLabel}>Total Organizations</span>
          </div>
        </div>
      )}

      {/* Table */}
      <Card>
        {loading ? (
          <Loader />
        ) : (
          <Table
            data={tableData}
            show={['name', 'plan', 'status', 'owner_email', 'user_count', 'employee_count', 'created']}
            badge={{ col: 'status', color: 'blue', condition: [{ value: 'Active', color: 'green' }, { value: 'Inactive', color: 'red' }] }}
            actions={{
              custom: [
                {
                  icon: 'eye',
                  action: (row) => handleViewWorkspace(row),
                  title: 'View Details'
                },
                {
                  icon: 'power',
                  action: (row) => handleToggleStatus(row),
                  title: 'Toggle Status'
                }
              ]
            }}
          />
        )}
      </Card>

      {/* Pagination */}
      {data && data.total > filters.limit && (
        <Paginate
          offset={(filters.page - 1) * filters.limit}
          limit={filters.limit}
          total={data.total}
          onChange={handlePageChange}
        />
      )}
    </Animate>
  );
}